import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { ButtonSize } from "./Types/Buttons.types";
import { handleFontSize, handleHeight } from "./utils";

export type ButtonTypology =
  "primary" | "secondary" | "tertiary" | "tertiaryIcon" | "iconOnly";

type SxEntry =
  | boolean
  | SystemStyleObject<Theme>
  | ((theme: Theme) => SystemStyleObject<Theme>);

const FOCUS = "&:focus-visible, &.Mui-focusVisible";
const DISABLED = "&.Mui-disabled";

const getColors = ({ palette }: Theme) => ({
  ctaBackground: palette.backgroundPrimaryCTA,
  ctaBackgroundHover: palette.backgroundPrimaryCTAHover,
  ctaText: palette.text.primaryCta,
  ctaBorder: palette.borderPrimaryCta ?? palette.primary.main,
  ctaBorderFocus:
    palette.borderPrimaryCtaHover ?? palette.backgroundPrimaryCTAHover,
  surface: palette.backgroundPrimary ?? palette.background.default,
  surfaceHover: palette.backgroundSecondaryCTAHover ?? palette.action.hover,
  text: palette.text.primary,
  textDisabled: palette.text.disabled,
  link: palette.systemSecondary01 ?? palette.primary.main,
  disabledBackground: palette.backgroundDisabled,
  disabledBackgroundAlternative:
    palette.backgroundDisabledAlternative ?? palette.backgroundDisabled,
});

type Colors = ReturnType<typeof getColors>;

const textBase = (size: ButtonSize): SystemStyleObject<Theme> => ({
  height: handleHeight(size),
  fontSize: handleFontSize(size),
  fontWeight: 600,
  fontStyle: "normal",
  lineHeight: "1.5rem",
  borderRadius: "8px",
  textTransform: "none",
  padding: "0 1rem",
});

const iconBase = (size: ButtonSize): SystemStyleObject<Theme> => ({
  height: handleHeight(size),
  minWidth: "unset",
  borderRadius: "8px",
  padding: 0,
});

const filled = (c: Colors): SystemStyleObject<Theme> => ({
  backgroundColor: c.ctaBackground,
  color: c.ctaText,
  border: "2px solid transparent",
  "&:hover": { backgroundColor: c.ctaBackgroundHover },
  [FOCUS]: { borderColor: c.ctaBorderFocus },
  "&:active": {
    backgroundColor: c.ctaBackgroundHover,
    borderColor: c.ctaBorder,
  },
  [DISABLED]: {
    opacity: 0.3,
    backgroundColor: c.disabledBackground,
    color: c.textDisabled,
    borderColor: "transparent",
  },
});

const outlined = (c: Colors): SystemStyleObject<Theme> => ({
  backgroundColor: c.surface,
  color: c.text,
  border: `2px solid ${c.text}`,
  "&:hover": { backgroundColor: c.surfaceHover },
  [FOCUS]: { borderColor: c.ctaBorder },
  "&:active": { backgroundColor: c.surface, borderColor: c.ctaBorder },
  [DISABLED]: {
    opacity: 0.3,
    backgroundColor: c.disabledBackgroundAlternative,
    color: c.text,
    borderColor: c.text,
  },
});

const link = (c: Colors, underline = false): SystemStyleObject<Theme> => {
  const textDecoration = underline ? "underline" : "none";
  return {
    backgroundColor: "transparent",
    color: c.link,
    border: "1px solid transparent",
    textDecoration,
    textUnderlineOffset: "3px",
    "&:hover": {
      backgroundColor: "transparent",
      color: c.text,
      textDecoration,
    },
    [FOCUS]: { color: c.text, borderColor: c.ctaBorder },
    "&:active": { color: c.text },
    [DISABLED]: {
      opacity: 0.3,
      backgroundColor: "transparent",
      color: c.text,
    },
  };
};

export const getButtonSx = (
  theme: Theme,
  typology: ButtonTypology,
  size: ButtonSize,
): SystemStyleObject<Theme> => {
  const c = getColors(theme);
  switch (typology) {
    case "primary":
      return { ...textBase(size), ...filled(c) };
    case "secondary":
      return { ...textBase(size), ...outlined(c) };
    case "tertiary":
      return { ...textBase(size), ...link(c, true) };
    case "tertiaryIcon":
      return { ...iconBase(size), padding: "0 0.5rem", ...link(c) };
    case "iconOnly":
      return { ...iconBase(size), width: handleHeight(size), ...outlined(c) };
  }
};

const toEntries = (sx: SxProps<Theme>): SxEntry[] =>
  Array.isArray(sx) ? [...(sx as ReadonlyArray<SxEntry>)] : [sx as SxEntry];

export const mergeSx = (
  ...items: Array<SxProps<Theme> | undefined>
): SxProps<Theme> =>
  items.reduce<SxEntry[]>(
    (acc, item) => (item ? acc.concat(toEntries(item)) : acc),
    [],
  );
